import React, { useState, useEffect } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  SvgIcon,
  Stack,
  Link,
} from "@mui/material";
import moment from "moment";
import SearchIcon from "@mui/icons-material/Search";
import LaunchIcon from "@mui/icons-material/Launch";

import ActionBtnGroup from "./ActionBtnGroup";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { ReactComponent as DesktopIcon } from "../../../../assets/desktop.svg";
import InfoPanelGroup from "./InfoPanelGroup";
import { IconButton, Tooltip } from "../../../../components";

import {
  BORDER_COLORS,
  DATETIME_FORMAT,
  SUPPORTED_COUNTRIES,
  TCountryCode,
} from "../../../../constants";
import { ILookup } from "../../../../features/lookups/lookups.types";

interface IHistoryCardProps {
  item: ILookup;
  isShowAll: boolean;
}

const HistoryCard: React.FC<IHistoryCardProps> = ({ item, isShowAll }) => {
  const navigate = useNavigate();
  const [isShowDetail, setIsShowDetail] = useState<boolean>(false);

  const handleAnalyze = () => {
    navigate(`/product-analyzer?asin=${item.asin}&lookupId=${item.lookupId}`);
  };

  useEffect(() => {
    setIsShowDetail(isShowAll);
  }, [isShowAll]);

  const renderFlagIcon = (countryCode: TCountryCode) => {
    const country = SUPPORTED_COUNTRIES.find(
      (country) => country.code === countryCode,
    );
    if (country) {
      return <img src={country.icon} alt={country.name} width="15px" />;
    } else {
      return null;
    }
  };

  const renderTitle = () => {
    return (
      <Link
        component={RouterLink}
        to={`/product-analyzer?asin=${item.asin}&lookupId=${item.lookupId}`}
        underline="hover"
        color="inherit"
      >
        <Typography
          variant="subtitle1"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.title}
        </Typography>
      </Link>
    );
  };

  const renderDetails = () => {
    return (
      <Box
        display={"flex"}
        gap={"4px"}
        alignItems={"center"}
        flexDirection={"row"}
      >
        {renderFlagIcon("US")}
        <DesktopIcon />
        <Typography variant="body1">{item.asin}</Typography>
      </Box>
    );
  };
  const renderUpdatedAt = () => {
    return (
      <Typography variant="caption">
        {moment(item.updatedAt).format(DATETIME_FORMAT)}
      </Typography>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: {
          xs: "column",
          sm: "row",
        },
        alignItems: "stretch",
        borderBottom: `1px solid ${BORDER_COLORS.main}`,
        borderRadius: 0,
        "&:last-of-type": {
          borderBottom: "none",
        },
        cursor: "pointer",
      }}
      onClick={handleAnalyze}
    >
      <Box
        sx={{
          borderRight: { xs: "none", sm: `1px solid ${BORDER_COLORS.main}` },
          display: { xs: "none", sm: "flex" },
          alignItems: "center",
          justifyContent: "center",
          width: { xs: "100%", sm: "120px" },
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        <img
          src={item.imageURL ?? "images/history-client.svg"}
          onClick={(e) => {
            e.stopPropagation();
            navigate(
              `/product-analyzer?asin=${item.asin}&lookupId=${item.lookupId}`,
            );
          }}
          alt={item.title}
          style={{
            width: "100%",
            maxWidth: "120px",
            maxHeight: "120px",
            objectFit: "contain",
          }}
        />
      </Box>

      <Stack display={{ xs: "none", md: "flex" }} direction="row" flexGrow={1}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            padding: "8px",
          }}
        >
          {/* Title link should not trigger parent navigation bubble */}
          <Box onClick={(e) => e.stopPropagation()}>{renderTitle()}</Box>

          {renderDetails()}

          <Box marginTop="16px">
            {/* Info panel interactions shouldn't trigger parent navigation */}
            <Box onClick={(e) => e.stopPropagation()}>
              <InfoPanelGroup item={item} />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
            width: "130px",
            padding: "8px",
            borderLeft: `1px solid ${BORDER_COLORS.main}`,
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            width="100%"
            gap="8px"
            onClick={(e) => e.stopPropagation()}
          >
            <ActionBtnGroup item={item} />
            <Divider sx={{ width: "100%" }} />
            <Button
              variant="contained"
              fullWidth
              onClick={(e) => {
                e.stopPropagation();
                handleAnalyze();
              }}
            >
              <SvgIcon component={SearchIcon} sx={{ fontSize: "15px" }} />
              &nbsp;Analyze
            </Button>
          </Box>

          {renderUpdatedAt()}
        </Box>
      </Stack>

      <Stack
        display={{ xs: "flex", md: "none" }}
        direction="column"
        flexGrow={1}
      >
        {/* Top row: image (xs only) + title/details/buttons */}
        <Box
          display="flex"
          flexDirection="row"
          gap="8px"
          padding="8px 8px 0 8px"
        >
          {/* Image - xs only */}
          <Box
            sx={{
              display: { xs: "flex", sm: "none" },
              width: "80px",
              flexShrink: 0,
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              navigate(
                `/product-analyzer?asin=${item.asin}&lookupId=${item.lookupId}`,
              );
            }}
          >
            <img
              src={item.imageURL ?? "images/history-client.svg"}
              alt={item.title}
              style={{
                width: "100%",
                maxWidth: "80px",
                maxHeight: "80px",
                objectFit: "contain",
              }}
            />
          </Box>

          {/* Right: title + details + buttons */}
          <Box display="flex" flexDirection="column" flexGrow={1} gap="4px">
            {/* Title row: title + Analyze icon (xs only) */}
            <Box
              display="flex"
              flexDirection="row"
              alignItems="flex-start"
              gap="4px"
            >
              <Box flexGrow={1} onClick={(e) => e.stopPropagation()}>
                {renderTitle()}
              </Box>
              <Box display={{ xs: "flex", sm: "none" }} flexShrink={0}>
                <Tooltip title="Analyze">
                    <LaunchIcon
                      sx={{ fontSize: "18px", color: "#484848" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAnalyze();
                      }}
                    />
                </Tooltip>
              </Box>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              {renderDetails()}
              {/* Date on sm only — xs shows it below with show-more icon */}
              <Box display={{ xs: "none", sm: "block" }}>
                {renderUpdatedAt()}
              </Box>
            </Box>

            {/* xs: ActionBtnGroup */}
            <Box
              display={{ xs: "flex", sm: "none" }}
              flexDirection="row"
              alignItems="center"
              gap="8px"
              marginTop="4px"
              onClick={(e) => e.stopPropagation()}
            >
              <ActionBtnGroup item={item} />
            </Box>

            {/* xs: ShowMore icon + Date */}
            <Box
              display={{ xs: "flex", sm: "none" }}
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsShowDetail((prev) => !prev);
                }}
                sx={{ cursor: "pointer", color: "#484848" }}
              >
                <Typography variant="caption">
                  {isShowDetail ? "Show less details" : "Show more details"}
                </Typography>
                {isShowDetail ? (
                  <ArrowDropUpIcon sx={{ fontSize: "24px" }} />
                ) : (
                  <ArrowDropDownIcon sx={{ fontSize: "24px" }} />
                )}
              </Box>
              {renderUpdatedAt()}
            </Box>

            {/* sm: ActionBtnGroup + Analyze + ShowMore buttons */}
            <Box
              display={{ xs: "none", sm: "flex" }}
              gap="8px"
              alignItems="center"
              marginY="4px"
              onClick={(e) => e.stopPropagation()}
            >
              <ActionBtnGroup item={item} />
              <Button
                variant="contained"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAnalyze();
                }}
              >
                <SearchIcon sx={{ fontSize: "16px" }} />
                &nbsp;Analyze
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsShowDetail((prev) => !prev);
                }}
              >
                {isShowDetail ? (
                  <>
                    Hide details <ArrowDropUpIcon sx={{ fontSize: "20px" }} />
                  </>
                ) : (
                  <>
                    Show details <ArrowDropDownIcon sx={{ fontSize: "20px" }} />
                  </>
                )}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Stats grid at bottom */}
        <Box
          sx={{
            transition: "all 0.3s ease-in-out",
            maxHeight: !isShowDetail ? "0px" : "1000px",
            overflow: "hidden",
            marginBottom: isShowDetail ? "8px" : 0,
            padding: "0 8px",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <InfoPanelGroup item={item} />
        </Box>
      </Stack>
    </Paper>
  );
};

export default HistoryCard;
